import type { MatchEvent, Projectile } from "../game/match-controller";

/** Event-driven HUD state (ticks, 60 Hz) that the state snapshot cannot show. */
export interface HudFx {
  /** Tick of the last PERFECT PACK ball the player packed. */
  perfectAt: number | null;
  /** Tick the player's ball count last went up (R2 sparkle / tray pop). */
  packAt: number | null;
  /** Result of the last defensive press. */
  feedback: { kind: "blocked" | "dodged" | "early"; at: number } | null;
}

export const newHudFx = (): HudFx => ({
  perfectAt: null,
  packAt: null,
  feedback: null,
});

export interface DefenceTiming {
  /** Ticks the dodge / block window stays active after a press. */
  dodgeWindow: number;
  blockWindow: number;
}

/** Player-targeted snowballs still in the air. */
export const incomingBalls = (projectiles: Projectile[], tick: number) =>
  projectiles.filter((p) => p.target === "player" && p.endTick > tick);

export function applyHudEvent(
  fx: HudFx,
  event: MatchEvent,
  tick: number,
  projectiles: Projectile[],
  timing: DefenceTiming,
): void {
  switch (event.type) {
    case "ammo":
      if (event.player === "player") {
        fx.packAt = tick;
        if (event.perfect) fx.perfectAt = tick;
      }
      break;
    case "blocked":
      if (event.player === "player")
        fx.feedback = { kind: "blocked", at: tick };
      break;
    case "dodged":
      if (event.player === "player") fx.feedback = { kind: "dodged", at: tick };
      break;
    case "dodgeActivated":
    case "blockActivated": {
      if (event.player !== "player") break;
      const window =
        event.type === "dodgeActivated"
          ? timing.dodgeWindow
          : timing.blockWindow;
      // Pressed while a ball is in flight but its arrival is after the window
      // would close: the press is wasted.
      if (
        incomingBalls(projectiles, tick).some((p) => p.endTick > tick + window)
      )
        fx.feedback = { kind: "early", at: tick };
      break;
    }
    default:
      break;
  }
}

export const FEEDBACK_TICKS = 72;
export const POPUP_TICKS = 72;

/** Seconds readout for a 0..1 cooldown fraction, one decimal. */
export const cooldownSeconds = (fraction: number, totalSeconds: number) =>
  Math.max(0, fraction * totalSeconds).toFixed(1) + "s";
